import { SnapshotData } from 'ais-facade-api/api';
import { useGetShipInfoQuery } from 'app/api/ships';
import { AtonCardHeader } from './AtonCardHeader';
import { useGetMapSnapshotMutation } from 'app/api/facade';
import { InfoWrapper } from 'app/components/AISCard';
import { useMemo } from 'react';
import { glToStringHDMS } from 'app/helpers/getHDMS';
import { useAppSelector } from 'app/store/store';
import { useTranslation } from 'react-i18next';
import { Icon } from 'app/components/Icon';
import { sortAreas } from 'app/helpers/sortAreasByTypeAndAlphabet';
import { useFormatMoment } from 'app/hooks/useFormatMoment';
import moment from 'moment';
import { AccordionComponent } from '../Accordion/AccordionComponent';
import { CountryFlag } from 'CountryFlag/CountryFlag';

import styles from './AtonCard.module.scss';

interface AtonCardProps {
  className?: string;
  isHistoryMode?: boolean;
  mmsi: number;
  shown?: boolean;
  onClose: () => void;
  onClick?: () => void;
  snapshotData?: SnapshotData;
}

export const AtonCard = ({ isHistoryMode, mmsi, shown, onClose, onClick, snapshotData }: AtonCardProps) => {
  const { t } = useTranslation();
  const formatMoment = useFormatMoment();

  const { data: aton, isLoading, ...atonInfoRequest } = useGetShipInfoQuery(mmsi, { skip: isHistoryMode });

  const [, snapshotRequest] = useGetMapSnapshotMutation({
    fixedCacheKey: 'get-map-snapshot-mutation',
  });

  const isShown = shown && ((!isLoading && aton?.result) || snapshotData);

  const profile = useAppSelector((state) => state.userSlice.profile);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);

  const handleRefetch = () => {
    if (isHistoryMode) {
      if (!snapshotRequest.isUninitialized) {
        snapshotRequest.reset();
      }
    } else {
      if (!atonInfoRequest.isUninitialized) {
        atonInfoRequest.refetch();
      }
    }
  };

  const position: string | undefined = useMemo(() => {
    if (snapshotData && typeof snapshotData.lat === 'number' && typeof snapshotData.lon === 'number') {
      return glToStringHDMS([snapshotData.lon, snapshotData.lat], coordinatesFormat);
    }
    if (typeof aton?.result?.position?.latitude === 'number' && typeof aton?.result?.position?.longitude === 'number') {
      return glToStringHDMS([aton.result.position.longitude, aton.result.position.latitude], coordinatesFormat);
    } else {
      return undefined;
    }
  }, [aton?.result?.position, snapshotData, coordinatesFormat]);
  const source = aton?.result?.meta?.source;
  const safety = aton?.result?.safety;
  const lastUpload = useMemo(() => {
    let dataResource;
    if (snapshotData && snapshotData.t !== undefined) {
      dataResource = snapshotData.t;
    }
    if (aton?.result?.t !== undefined) {
      dataResource = aton.result.t;
    }
    return formatMoment(moment(dataResource));
  }, [snapshotData, aton?.result, formatMoment]);


  return (
    <>
      {isShown && (
        <div className={styles.className}>
          <div className={styles.wrapper}>
            <AtonCardHeader onClose={onClose} atonInfo={snapshotData || aton?.result} refetchFunc={handleRefetch} />
            {typeof safety?.lastSignal === 'string' && (
              <div className={styles.safety_block} title={safety?.text}>
                {<span className={styles.label_icon}>{<Icon size="14px" name="safety" />}</span>}
                {t('Alarm')}
              </div>
            )}
            <div className={styles.content}>
              {typeof safety?.lastSignal === 'string' && (
                <div className={styles.safety_container}>
                  <InfoWrapper
                    labelIcon={<Icon size="14px" name="safety" />}
                    label={`${t('AlarmMessage')}:`}
                    title={safety?.text}
                    value={formatMoment(moment(safety.lastSignal))}
                  />
                </div>
              )}
              <InfoWrapper
                clickable={!!position}
                label={`${t('Position')}:`}
                value={position}
                valueIcon={<Icon name="aim" size="14px" color={'var(--default-primary)'} />}
                onClick={onClick}
              />
              {!isHistoryMode && (
                <InfoWrapper
                  label={`${t('Areas')}:`}
                  value={
                    aton?.result?.inAreas && aton?.result?.inAreas.length > 0 ? (
                      <div className={styles.geofence_info}>
                        {sortAreas(aton?.result?.inAreas, 'USER')
                          .concat(sortAreas(aton?.result?.inAreas, 'COMMON'))
                          .map((area) => area?.name || '')
                          .join(', ')}
                      </div>
                    ) : undefined
                  }
                />
              )}
              <InfoWrapper label={`${t('DataSourceLabel')}:`} value={source} />
              <InfoWrapper
                label={`${t('DataObtained')}:`}
                value={lastUpload}
                title={typeof aton?.result?.sourceCode === 'number' ? `${t('Source')}: ${aton.result.sourceCode}` : undefined}
              />
            </div>

            <AccordionComponent label={`${t('Registration')}:`} aid={2} parent="ais_card">
              {!isHistoryMode && (
                <InfoWrapper
                  label={`${t('Flag')}:`}
                  value={
                    typeof aton?.result?.meta?.countryName !== 'undefined' ? (
                      <>
                        {aton.result?.meta?.countryCode && (
                          <CountryFlag countryCode={aton.result.meta.countryCode.toLowerCase()} />
                        )}
                        <strong className={styles.countryName} title={aton.result?.meta?.countryName}>
                          {aton.result?.meta?.countryName} ({aton.result?.meta?.countryCode})
                        </strong>
                      </>
                    ) : null
                  }
                />
              )}
              <InfoWrapper label={`${t('MMSI')}:`} value={snapshotData?.mmsi || aton?.result?.mmsi} strong />
            </AccordionComponent>
          </div>
        </div>
      )}
    </>
  );
};
